import React, { useState, useEffect } from 'react';
import { Grid, Box, Paper, Typography, LinearProgress } from '@mui/material';
import { useTranslation } from 'react-i18next';
import {
  MonetizationOn as MonetizationOnIcon,
  Receipt as ReceiptIcon,
  TrendingUp as TrendingUpIcon,
  Person as PersonIcon,
  Assessment as AssessmentIcon,
  EmojiEvents as TrophyIcon,
  Lock as LockIcon,
} from '@mui/icons-material';

import PageHeader from '../PageHeader/PageHeader';
import SummaryCards from '../Reports/SummaryCards';
import MonthYearSelector from '../Reports/Sales/MonthYearSelector';
import StaffAchievementsService from '../../services/StaffAchievementsService';
import { getProfile } from '../../services/UserService';
import { permissionOptions } from '../../utils/Enums';
import dayjs from 'dayjs';
import SnackbarUtils from '../../utils/SnackbarUtils';
import {
  MonthlyTrendChart,
  DailySalesChart,
  MonthlyGoalCard,
  AchievementsCard
} from './SellerPerformanceCharts';
import { formatMoney } from '../../utils/currency';

const SellerPerformanceDashboard = () => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [dashboardData, setDashboardData] = useState(null);
  const [month, setMonth] = useState(dayjs().month() + 1);
  const [year, setYear] = useState(dayjs().year());
  const [userEmail, setUserEmail] = useState(null);
  const [hasViewObjectivesPermission, setHasViewObjectivesPermission] = useState(false);

  // Verificar permisos y obtener email del usuario
  useEffect(() => {
    const checkPermissions = () => {
      try {
        const userPermissions = JSON.parse(localStorage.getItem('permissions') || '[]');
        const hasPermission = (permissionId) => userPermissions.includes(permissionId);
        
        const viewObjectivesId = permissionOptions.find(p => p.label === 'VIEW_OBJECTIVES')?.id;
        setHasViewObjectivesPermission(hasPermission(viewObjectivesId));
      } catch (error) {
        console.error('Error checking permissions:', error);
        setHasViewObjectivesPermission(false);
      }
    };

    const fetchUserProfile = async () => {
      try {
        const profile = await getProfile();
        setUserEmail(profile.email);
      } catch (error) {
        console.error('Error fetching user profile:', error);
      }
    };

    checkPermissions();
    fetchUserProfile();
  }, []);

  useEffect(() => {
    if (userEmail !== null) {
      fetchData();
    }
  }, [month, year, userEmail, hasViewObjectivesPermission]);

  const fetchData = async () => {
    try {
      setLoading(true);
      
      // Solo usar el endpoint del backend si tiene permiso VIEW_OBJECTIVES
      if (hasViewObjectivesPermission && userEmail) {
        const data = await StaffAchievementsService.getStaffAchievements(userEmail, month, year);
        // Transformar datos del backend al formato esperado
        setDashboardData(transformBackendData(data));
      } else {
        // Si no tiene permiso, no mostrar datos
        setDashboardData(null);
      }
    } catch (error) {
      console.error('Error fetching seller performance data:', error);
      SnackbarUtils.error(t('error_loading_data') || 'Error al cargar los datos');
      setDashboardData(null);
    } finally {
      setLoading(false);
    }
  };


  // Transformar datos del backend al formato esperado por los componentes
  const transformBackendData = (backendData) => {
    console.log('[SellerPerformanceDashboard] TransformBackendData - Raw data:', backendData);
    
    // La estructura del backend es: { month, year, userProfile: { email }, sellerPerformance: { summary, objectives, achievements, monthlyTrend, dailySales } }
    const { userProfile, sellerPerformance } = backendData;
    const summary = sellerPerformance?.summary || {};
    const objectives = sellerPerformance?.objectives || [];
    const achievements = sellerPerformance?.achievements || [];
    const monthlyTrend = sellerPerformance?.monthlyTrend || [];
    const dailySales = sellerPerformance?.dailySales || [];
    
    // Métricas del summary
    const currentTotal = summary.total ?? 0;
    const ticketCount = summary.ticketCount ?? 0;
    const averageTicket = summary.averageTicket ?? 0;
    const newClients = summary.newClients ?? 0;

    // Transformar objetivos (ya vienen en el formato correcto del backend)
    const transformedObjectives = objectives.map(obj => ({
      id: obj.id,
      title: obj.title,
      description: obj.description,
      targetValue: obj.targetValue ?? 0,
      category: obj.category,
      actualValue: obj.actualValue ?? 0,
      progress: obj.progress ?? 0
    }));

    // Transformar achievements
    const transformedAchievements = achievements.map(achievement => ({
      id: `achievement_${achievement.id}`,
      name: achievement.name,
      description: achievement.description,
      icon: getIconByType(achievement.icon ?? achievement.iconType ?? achievement.icon_type),
      category: achievement.category,
      completed: achievement.completed ?? achievement.unlocked ?? false,
      progress: Math.min(achievement.progress || 0, 100),
      completedDate: achievement.completedDate ?? achievement.unlockedAt ?? achievement.unlocked_at ?? null
    }));

    // Transformar monthly trend (ya viene con month y year como números)
    const transformedMonthlyTrend = (monthlyTrend || []).map(trend => ({
      month: trend.month,
      year: trend.year,
      total: trend.total || 0,
      tickets: trend.tickets || 0
    }));

    // Transformar daily sales (el backend envía date como string "2025-11-12")
    const transformedDailySales = (dailySales || []).map(sale => ({
      day: sale.date ? dayjs(sale.date).date() : null,
      sales: sale.total ?? 0,
      tickets: sale.tickets || 0
    }));

    // Calcular valores para compatibilidad con el código existente
    const salesObjective = objectives.find(obj => obj.category === 'sales');
    const monthlyGoal = salesObjective?.targetValue ?? 0;
    const goalProgress = monthlyGoal > 0 ? (currentTotal / monthlyGoal) * 100 : 0;

    // Datos de progreso semanal (calcular desde dailySales usando las fechas reales)
    let weeklyTotal = 0;
    let lastWeekTotal = 0;
    
    if (dailySales && dailySales.length > 0) {
      // Reconstruir las fechas completas para cada venta diaria
      const salesWithDates = dailySales.map(sale => {
        const saleDate = dayjs(sale.date);
        return {
          ...sale,
          fullDate: saleDate,
          day: saleDate.date(),
          total: sale.total || 0
        };
      });
      
      // Ordenar por fecha descendente (más reciente primero)
      const sortedSales = salesWithDates.sort((a, b) => b.fullDate - a.fullDate);
      
      if (sortedSales.length > 0) {
        // Fecha más reciente (último día con datos)
        const mostRecentDate = sortedSales[0].fullDate;
        
        // Calcular rango de la semana actual (últimos 7 días desde la fecha más reciente)
        const weekStart = mostRecentDate.subtract(6, 'days'); // Incluye el día más reciente
        
        // Calcular rango de la semana anterior (7 días antes de la semana actual)
        const previousWeekStart = weekStart.subtract(7, 'days');
        const previousWeekEnd = weekStart.subtract(1, 'days');
        
        // Sumar ventas de la semana actual
        weeklyTotal = sortedSales
          .filter(sale => {
            const saleDate = sale.fullDate;
            return (saleDate.isAfter(weekStart) || saleDate.isSame(weekStart)) && 
                   (saleDate.isBefore(mostRecentDate) || saleDate.isSame(mostRecentDate));
          })
          .reduce((sum, sale) => sum + sale.total, 0);
        
        // Sumar ventas de la semana anterior
        lastWeekTotal = sortedSales
          .filter(sale => {
            const saleDate = sale.fullDate;
            return (saleDate.isAfter(previousWeekStart) || saleDate.isSame(previousWeekStart)) && 
                   (saleDate.isBefore(previousWeekEnd) || saleDate.isSame(previousWeekEnd));
          })
          .reduce((sum, sale) => sum + sale.total, 0);
      }
    }

    console.log('[SellerPerformanceDashboard] TransformBackendData - Transformed values:', {
      currentTotal,
      ticketCount,
      averageTicket,
      newClients,
      objectives: transformedObjectives,
      achievements: transformedAchievements,
      monthlyTrend: transformedMonthlyTrend,
      dailySales: transformedDailySales,
      weeklyTotal,
      lastWeekTotal
    });

    return {
      userProfile: {
        id: null,
        first_name: null,
        last_name: null,
        email: userProfile?.email || null
      },
      sellerPerformance: {
        total: currentTotal,
        ticketCount: ticketCount,
        averageTicket: averageTicket,
        previousTotal: 0, // Se calcularía comparando con mes anterior
        variation: '0',
        monthlyTrend: transformedMonthlyTrend,
        dailySales: transformedDailySales,
        monthlyGoal: monthlyGoal,
        goalProgress: goalProgress,
        weeklyTotal: weeklyTotal,
        lastWeekTotal: lastWeekTotal,
        monthProjection: 0,
        achievements: transformedAchievements,
        bestDay: transformedDailySales.length > 0 ? Math.max(...transformedDailySales.map(d => d.sales)) : 0,
        bestDayDate: transformedDailySales.length > 0 ? transformedDailySales.find(d => d.sales === Math.max(...transformedDailySales.map(d => d.sales)))?.day : null,
        summary: {
          total: currentTotal,
          ticketCount: ticketCount,
          averageTicket: averageTicket,
          newClients: newClients
        }
      },
      objectives: transformedObjectives, // Incluir todos los objetivos transformados
      month: backendData.month || month,
      year: backendData.year || year
    };
  };

  // Mapear icon_type a emojis (esto debería venir del backend o ser configurable)
  const getIconByType = (iconType) => {
    const iconMap = {
      1: '💰',
      2: '⭐',
      3: '🏆',
      4: '👑',
      5: '🎫',
      6: '🎯',
      7: '🚀',
      8: '💫',
      9: '📈',
      10: '📊',
      11: '💥',
      12: '💎',
      13: '💠',
      14: '✨',
      15: '🎯',
      16: '🔥',
      17: '⚡',
      18: '🌟'
    };
    return iconMap[iconType] || '🏅';
  };

  const sellerData = dashboardData?.sellerPerformance || null;
  const userProfile = dashboardData?.userProfile || null;
  
  // Obtener newClients del summary del backend
  const backendSummary = dashboardData?.sellerPerformance?.summary;
  const newClients = backendSummary?.newClients ?? 0;

  const summaryItems = sellerData
    ? [
        {
          title: t('total_sales_month') || 'Ventas del mes',
          icon: <MonetizationOnIcon color="primary" fontSize="large" />,
          value: `${formatMoney(sellerData.total ?? 0)}`,
        },
        {
          title: t('tickets_month') || 'Tickets del mes',
          icon: <ReceiptIcon color="success" fontSize="large" />,
          value: sellerData.ticketCount || 0,
        },
        {
          title: t('average_ticket') || 'Ticket promedio',
          icon: <AssessmentIcon color="info" fontSize="large" />,
          value: `${formatMoney(sellerData.averageTicket ?? 0)}`,
        },
        {
          title: t('new_clients') || 'Nuevos Clientes',
          icon: <PersonIcon color="secondary" fontSize="large" />,
          value: newClients || 0,
        },
      ]
    : [
        {
          title: t('total_sales_month') || 'Ventas del mes',
          icon: <MonetizationOnIcon color="primary" fontSize="large" />,
          value: t('no_data') || 'Sin datos',
        },
        {
          title: t('tickets_month') || 'Tickets del mes',
          icon: <ReceiptIcon color="success" fontSize="large" />,
          value: t('no_data') || 'Sin datos',
        },
        {
          title: t('average_ticket') || 'Ticket promedio',
          icon: <AssessmentIcon color="info" fontSize="large" />,
          value: t('no_data') || 'Sin datos',
        },
        {
          title: t('new_clients') || 'Nuevos Clientes',
          icon: <PersonIcon color="secondary" fontSize="large" />,
          value: t('no_data') || 'Sin datos',
        },
      ];

  return (
    <Box p={3} mb={10}>
      <Grid container spacing={2} alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
        <Grid item>
          <PageHeader
            title={t('my_performance') || 'Mi Rendimiento'}
            subtitle={t('my_performance_subtitle') || 'Seguimiento de tu desempeño como vendedor'}
          />
        </Grid>
        {hasViewObjectivesPermission && (
          <Grid item>
            <MonthYearSelector month={month} setMonth={setMonth} year={year} setYear={setYear} />
          </Grid>
        )}
      </Grid>

      {/* Información del usuario */}
      {hasViewObjectivesPermission && userProfile && (
        <Paper elevation={2} sx={{ p: 2, mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <PersonIcon color="primary" fontSize="large" />
            <Box>
              <Typography variant="h6">
                {userProfile.first_name || ''} {userProfile.last_name || ''}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {userProfile.email || ''}
              </Typography>
            </Box>
          </Box>
        </Paper>
      )}

      {/* Tarjetas de resumen */}
      {hasViewObjectivesPermission && (
        <Box mt={2}>
          <SummaryCards items={summaryItems} loading={loading} />
        </Box>
      )}

      {/* Logros y Gráficos */}
      {hasViewObjectivesPermission && sellerData && (
        <Grid container spacing={2} mt={3}>
          {/* Columna izquierda: Logros (50% de la pantalla) */}
          <Grid item xs={12} md={6}>
            <AchievementsCard
              achievements={sellerData.achievements}
              loading={loading}
            />
          </Grid>

          {/* Columna derecha: Objetivos, Tendencia Mensual y Ventas Diarias (50% de la pantalla) */}
          <Grid item xs={12} md={6}>
            <Grid container spacing={2}>
              {/* Objetivos Mensuales */}
              <Grid item xs={12}>
                <MonthlyGoalCard
                  objectives={dashboardData?.objectives || []}
                  loading={loading}
                />
              </Grid>
              {/* Tendencia Mensual */}
              <Grid item xs={12}>
                <MonthlyTrendChart
                  data={sellerData.monthlyTrend}
                  loading={loading}
                />
              </Grid>
              {/* Ventas Diarias */}
              <Grid item xs={12}>
                <DailySalesChart
                  data={sellerData.dailySales}
                  loading={loading}
                />
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      )}

      {!hasViewObjectivesPermission && !loading && (
        <Paper sx={{ p: 6, mt: 4, textAlign: 'center' }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
            <LockIcon sx={{ fontSize: 80, color: 'text.secondary', opacity: 0.5 }} />
            <Typography variant="h5" gutterBottom>
              {t('no_objectives_permission') || 'Sin acceso a objetivos'}
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ maxWidth: 500 }}>
              {t('no_objectives_permission_message') || 'No tienes permiso para ver objetivos y logros. Contacta a tu administrador para obtener acceso.'}
            </Typography>
          </Box>
        </Paper>
      )}

      {hasViewObjectivesPermission && !sellerData && !loading && (
        <Paper sx={{ p: 4, mt: 4, textAlign: 'center' }}>
          <Typography variant="h6" gutterBottom>
            {t('no_performance_data') || 'No hay datos de rendimiento disponibles'}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t('no_performance_data_message') || 'No se encontraron datos de ventas para este período'}
          </Typography>
        </Paper>
      )}
    </Box>
  );
};

export default SellerPerformanceDashboard;
